import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const NAV = [
  { to: '/admin/dashboard',   icon: 'fas fa-tachometer-alt',  label: 'Dashboard',     roles: ['admin', 'editor', 'manager'] },
  { to: '/admin/messages',    icon: 'fas fa-envelope',        label: 'Messages',      roles: ['admin', 'manager'] },
  { to: '/admin/members',     icon: 'fas fa-id-card',         label: 'Members',       roles: ['admin', 'manager'] },
  { to: '/admin/broadcast',   icon: 'fas fa-paper-plane',     label: 'Email Members', roles: ['admin'] },
  { to: '/admin/events',      icon: 'fas fa-calendar-alt',    label: 'Events',        roles: ['admin', 'editor'] },
  { to: '/admin/gallery',     icon: 'fas fa-images',          label: 'Gallery',       roles: ['admin', 'editor'] },
  { to: '/admin/news',        icon: 'fas fa-newspaper',       label: 'News',          roles: ['admin', 'editor'] },
  { to: '/admin/documents',   icon: 'fas fa-file-alt',        label: 'Documents',     roles: ['admin', 'editor'] },
  { to: '/admin/partners',    icon: 'fas fa-handshake',       label: 'Partners',      roles: ['admin', 'editor'] },
  { to: '/admin/hero',        icon: 'fas fa-sliders-h',       label: 'Hero Banners',  roles: ['admin', 'editor'] },
  { to: '/admin/faq',         icon: 'fas fa-question-circle', label: 'FAQ',           roles: ['admin', 'editor'] },
  { to: '/admin/milestones',  icon: 'fas fa-trophy',          label: 'Milestones',    roles: ['admin', 'editor'] },
  { to: '/admin/about',        icon: 'fas fa-info-circle',     label: 'About',         roles: ['admin', 'editor'] },
  { to: '/admin/cricketclub', icon: 'fas fa-baseball-ball',   label: 'Cricket Club',  roles: ['admin', 'editor'] },
  { to: '/admin/content',     icon: 'fas fa-edit',            label: 'Site Settings', roles: ['admin', 'editor'] },
  { to: '/admin/media',       icon: 'fas fa-photo-video',     label: 'Media',         roles: ['admin', 'editor'] },
  { to: '/admin/users',       icon: 'fas fa-user-shield',     label: 'Users',         roles: ['admin'] },
];

export default function AdminLayout({ children }) {
  const { admin, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [showPwModal, setShowPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  const role = admin?.role || 'admin';
  const visibleNav = NAV.filter(item => item.roles.includes(role));
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const load = () => api.get('/feedback').then(r => setUnreadCount((r.data || []).filter(m => m.status === 'unread').length)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) return setPwError('New passwords do not match');
    setPwSaving(true); setPwError(''); setPwSuccess(false);
    try {
      await api.put('/auth/change-password', { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      setPwSuccess(true);
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPwError(err.response?.data?.error || 'Failed to change password');
    } finally { setPwSaving(false); }
  };

  const openPwModal = () => { setShowPwModal(true); setPwError(''); setPwSuccess(false); setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' }); };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="admin-panel d-flex" style={{ minHeight: '100vh', background: '#f4f6fb' }}>
      {/* Sidebar */}
      <div className="d-flex flex-column flex-shrink-0" style={{ width: collapsed ? '64px' : '240px', background: '#1B3A6B', transition: 'width 0.2s', minHeight: '100vh', overflowX: 'hidden' }}>
        <div className="d-flex align-items-center justify-content-between p-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          {!collapsed && <Link to="/" className="text-white text-decoration-none fw-bold small">MNCW Admin</Link>}
          <button className="btn btn-sm text-white p-1 ms-auto" onClick={() => setCollapsed(c => !c)}>
            <i className={`fas ${collapsed ? 'fa-bars' : 'fa-times'}`}></i>
          </button>
        </div>
        <nav className="flex-grow-1 py-2">
          {visibleNav.map(item => (
            <Link
              key={item.to}
              to={item.to}
              className="d-flex align-items-center px-3 py-2 text-decoration-none"
              style={{ fontSize: '0.9rem', transition: 'background 0.15s', background: pathname.startsWith(item.to) ? 'rgba(255,255,255,0.15)' : 'transparent', color: pathname.startsWith(item.to) ? '#ffffff' : 'rgba(255,255,255,0.55)' }}
              title={collapsed ? item.label : ''}
            >
              <i className={`${item.icon} me-3`} style={{ minWidth: '16px' }}></i>
              {!collapsed && <span>{item.label}</span>}
              {!collapsed && item.to === '/admin/messages' && unreadCount > 0 && (
                <span className="badge bg-danger ms-auto" style={{ fontSize: '0.65rem' }}>{unreadCount}</span>
              )}
            </Link>
          ))}
        </nav>
        <div className="p-3" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          {!collapsed && (
            <div className="text-white-50 small mb-2" style={{ fontSize: '11px' }}>
              {admin?.name} · <span className="text-capitalize">{role}</span>
            </div>
          )}
          <button onClick={handleLogout} className="btn btn-sm text-white-50 d-flex align-items-center gap-2 p-0">
            <i className="fas fa-sign-out-alt"></i>
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main */}
      <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
        {/* Topbar */}
        <div className="d-flex align-items-center justify-content-between px-4 py-3 bg-white shadow-sm">
          <div className="text-muted small">{pathname.split('/').filter(Boolean).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' / ')}</div>
          <div className="d-flex align-items-center gap-3">
            <Link to="/" target="_blank" className="btn btn-sm btn-outline-primary rounded-pill py-1 px-3">
              <i className="fas fa-external-link-alt me-1"></i>View Site
            </Link>
            <button className="btn btn-sm btn-outline-secondary rounded-pill py-1 px-3" onClick={openPwModal}>
              <i className="fas fa-key me-1"></i>Password
            </button>
            <span className="text-muted small">{admin?.email}</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow-1 p-4">
          {children}
        </div>
      </div>

      {/* Change Password Modal */}
      {showPwModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={() => setShowPwModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="fas fa-key me-2"></i>Change Password</h5>
                <button className="btn-close" onClick={() => setShowPwModal(false)}></button>
              </div>
              <div className="modal-body">
                {pwSuccess && <div className="alert alert-success py-2">Password changed successfully.</div>}
                {pwError && <div className="alert alert-danger py-2">{pwError}</div>}
                <form onSubmit={changePassword}>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Current Password</label>
                    <input type="password" className="form-control" value={pwForm.currentPassword} onChange={e => setPwForm(f => ({ ...f, currentPassword: e.target.value }))} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">New Password <span className="text-muted fw-normal">(min 8 chars)</span></label>
                    <input type="password" className="form-control" value={pwForm.newPassword} onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))} required minLength={8} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Confirm New Password</label>
                    <input type="password" className="form-control" value={pwForm.confirmPassword} onChange={e => setPwForm(f => ({ ...f, confirmPassword: e.target.value }))} required />
                  </div>
                  <div className="d-flex gap-2">
                    <button type="submit" disabled={pwSaving} className="btn btn-primary rounded-pill px-4">{pwSaving ? 'Saving…' : 'Change Password'}</button>
                    <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowPwModal(false)}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
